// src/pages/ITSchoolsLanding.js
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../../components/LandingHeader';
import AuthDialog from '../../components/AuthDialog';
import {
    Rocket, Database, Brain, Code,
    Lightbulb, TrendingUp, Award,
    Users, School, BookOpen
} from 'lucide-react';

const ITSchoolsLanding = () => {
    const { t } = useTranslation();
    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [initialAuthMode, setInitialAuthMode] = useState('register');
    const demoSectionRef = useRef(null);
    const videoRef = useRef(null);
    const [imageErrors, setImageErrors] = useState({});
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [activeTab, setActiveTab] = useState('school'); // для вкладок в секции benefits
    const [activeFeaturesTab, setActiveFeaturesTab] = useState('labs'); // для вкладок в секции features

    // Обработчик для кнопки регистрации
    const handleRegisterClick = () => {
        setInitialAuthMode('register');
        setShowAuthDialog(true);
    };

    // Функция обработки ошибок загрузки изображений
    const handleImageError = (imageName) => {
        setImageErrors(prev => ({
            ...prev,
            [imageName]: true
        }));
        console.error(`Ошибка загрузки изображения: ${imageName}`);
    };

    // Отложенная загрузка видео
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const options = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const source = document.createElement('source');
                    source.src = '/demos/product_demo.mp4';
                    source.type = 'video/mp4';
                    videoElement.appendChild(source);
                    videoElement.load();
                    videoElement.play().catch(err => console.error("Ошибка воспроизведения видео:", err));
                    observer.unobserve(videoElement);
                }
            });
        }, options);

        observer.observe(videoElement);

        return () => {
            if (videoElement) {
                observer.unobserve(videoElement);
            }
        };
    }, []);

    // Данные для секции проблем
    const challenges = [
        {
            icon: <BookOpen className="h-10 w-10 text-orange-500" />,
            title: t('itschools.challenges.practical_experience.title'),
            description: t('itschools.challenges.practical_experience.description')
        },
        {
            icon: <Users className="h-10 w-10 text-orange-500" />,
            title: t('itschools.challenges.staff_burnout.title'),
            description: t('itschools.challenges.staff_burnout.description')
        },
        {
            icon: <Award className="h-10 w-10 text-orange-500" />,
            title: t('itschools.challenges.talent_shortage.title'),
            description: t('itschools.challenges.talent_shortage.description')
        },
        {
            icon: <Rocket className="h-10 w-10 text-orange-500" />,
            title: t('itschools.challenges.long_development.title'),
            description: t('itschools.challenges.long_development.description')
        },
        {
            icon: <TrendingUp className="h-10 w-10 text-orange-500" />,
            title: t('itschools.challenges.competition.title'),
            description: t('itschools.challenges.competition.description')
        }
    ];

    // Данные для секции последствий
    const consequences = [
        {
            icon: <TrendingUp className="h-10 w-10 text-red-500 transform rotate-180" />,
            title: t('itschools.consequences.engagement.title'),
            description: t('itschools.consequences.engagement.description')
        },
        {
            icon: <Users className="h-10 w-10 text-red-500" />,
            title: t('itschools.consequences.graduates.title'),
            description: t('itschools.consequences.graduates.description')
        },
        {
            icon: <Award className="h-10 w-10 text-red-500" />,
            title: t('itschools.consequences.rating.title'),
            description: t('itschools.consequences.rating.description')
        }
    ];

    // Данные для секции преимуществ
    const benefits = [
        {
            icon: <TrendingUp className="h-10 w-10 text-green-500" />,
            title: t('itschools.benefits.quality.title'),
            description: t('itschools.benefits.quality.description')
        },
        {
            icon: <Award className="h-10 w-10 text-green-500" />,
            title: t('itschools.benefits.revenue.title'),
            description: t('itschools.benefits.revenue.description')
        },
        {
            icon: <Rocket className="h-10 w-10 text-green-500" />,
            title: t('itschools.benefits.competitive.title'),
            description: t('itschools.benefits.competitive.description')
        },
        {
            icon: <Users className="h-10 w-10 text-green-500" />,
            title: t('itschools.benefits.reputation.title'),
            description: t('itschools.benefits.reputation.description')
        },
        {
            icon: <Lightbulb className="h-10 w-10 text-green-500" />,
            title: t('itschools.benefits.independence.title'),
            description: t('itschools.benefits.independence.description')
        },
        {
            icon: <Award className="h-10 w-10 text-green-500" />,
            title: t('itschools.benefits.workload.title'),
            description: t('itschools.benefits.workload.description')
        },
        {
            icon: <Award className="h-10 w-10 text-green-500" />,
            title: t('itschools.benefits.completion.title'),
            description: t('itschools.benefits.completion.description')
        }
    ];

    return (
        <div className="min-h-screen min-w-screen w-full overflow-hidden bg-gray-900 text-white">
            <LandingHeader onLoginSuccess={() => {}} />

            {/* Hero Section */}
            <section className="pt-32 pb-20 relative overflow-hidden">
                {/* 3D Анимированный фон */}
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-700/30 via-orange-800/20 to-gray-900/10" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,theme(colors.orange.500/20),transparent_70%)]" />

                    {/* 3D плавающие элементы */}
                    <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-orange-500/5 blur-2xl animate-pulse" />
                    <div className="absolute bottom-1/3 left-1/3 w-96 h-96 rounded-full bg-orange-700/5 blur-3xl animate-pulse" />
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight tracking-tight text-white">
                            {t('itschools.hero.title')}
                        </h1>

                        <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
                            {t('itschools.hero.subtitle')}
                        </p>

                        <div className="flex justify-center">
                            <button
                                onClick={handleRegisterClick}
                                className="group relative px-10 py-5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold text-lg transition-all hover:scale-105 shadow-lg hover:shadow-orange-500/20 overflow-hidden"
                            >
                                {/* Анимация фона кнопки */}
                                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-700 opacity-0 group-hover:opacity-100 transition-opacity" />

                                <span className="relative z-10 flex items-center justify-center gap-3">
                                    {t('itschools.hero.start_free')}
                                    <Rocket className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Challenges Section */}
            <section className="py-20 bg-gray-900/80 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,theme(colors.orange.500/5),transparent_50%)]" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-orange-100 bg-clip-text text-transparent">
                            {t('itschools.challenges.title')}
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            {t('itschools.challenges.subtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {challenges.map((challenge, index) => (
                            <div
                                key={index}
                                className={`group relative overflow-hidden bg-gray-800/30 backdrop-blur-sm border border-gray-700 p-8 rounded-xl hover:border-orange-500/50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-orange-500/5 ${
                                    // Специальная обработка для последнего элемента, если общее число нечетное
                                    challenges.length % 3 !== 0 && index >= challenges.length - (challenges.length % 3)
                                        ? 'lg:col-span-1 lg:mx-auto lg:max-w-sm'
                                        : ''
                                }`}
                            >
                                {/* Декоративные элементы фона */}
                                <div className="absolute bottom-0 right-0 w-32 h-32 bg-orange-500/5 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                                <div className="relative z-10">
                                    {/* Контейнер для иконки с анимацией */}
                                    <div className="mb-6 w-16 h-16 rounded-xl bg-orange-500/10 flex items-center justify-center transform group-hover:scale-110 group-hover:bg-orange-500/20 transition-all duration-300 group-hover:rotate-3">
                                        {challenge.icon}
                                    </div>

                                    <h3 className="text-xl font-bold mb-4 group-hover:text-orange-400 transition-colors">
                                        {challenge.title}
                                    </h3>

                                    <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                        {challenge.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Consequences Section */}
            <section className="py-20 bg-gradient-to-b from-gray-800/40 to-gray-900/40 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_right,theme(colors.red.500/5),transparent_70%)]" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-red-100 bg-clip-text text-transparent">
                            {t('itschools.consequences.title')}
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            {t('itschools.consequences.subtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {consequences.map((consequence, index) => (
                            <div
                                key={index}
                                className="group relative overflow-hidden bg-gray-800/30 backdrop-blur-sm border border-gray-700 p-8 rounded-xl hover:border-red-500/50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-red-500/5"
                            >
                                {/* Декоративные элементы фона */}
                                <div className="absolute bottom-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                                <div className="relative z-10">
                                    <div className="mb-6 w-16 h-16 rounded-xl bg-red-500/10 flex items-center justify-center transform group-hover:scale-110 group-hover:bg-red-500/20 transition-all duration-300 group-hover:rotate-3">
                                        {consequence.icon}
                                    </div>
                                    <h3 className="text-xl font-bold mb-4 group-hover:text-red-400 transition-colors">
                                        {consequence.title}
                                    </h3>
                                    <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                        {consequence.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Solution Section */}
            <section className="py-20 bg-gray-900 relative overflow-hidden">
                {/* Фон с органическими формами, как в CTA секции */}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600/30 to-orange-800/30">
                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                        {/* SVG органические формы */}
                        <svg className="absolute top-0 left-0 w-full h-full opacity-20" viewBox="0 0 1000 1000" preserveAspectRatio="none">
                            <path d="M0,500 C150,300 350,0 500,500 C650,1000 850,700 1000,500 L1000,1000 L0,1000 Z" fill="url(#orange-gradient)" />
                            <defs>
                                <linearGradient id="orange-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#F97316" />
                                    <stop offset="100%" stopColor="#C2410C" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <div className="inline-block p-2 px-4 bg-orange-500/10 backdrop-blur-sm rounded-full text-orange-400 font-semibold mb-4 transform hover:scale-105 transition-transform">
                            {t('itschools.solution.tag')}
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold mb-6">
                            <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                                {t('itschools.solution.title')}
                            </span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {t('itschools.solution.subtitle')}
                        </p>
                    </div>

                    <div ref={demoSectionRef} className="max-w-4xl mx-auto mb-16">
                        <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-700 group">
                            {/* Блики и эффекты вокруг видео */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-orange-500/30 to-orange-600/30 blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>

                            <div className="relative">
                                <video
                                    ref={videoRef}
                                    autoPlay={false}
                                    loop
                                    muted
                                    playsInline
                                    className="w-full aspect-video object-cover bg-gray-800"
                                    onPlay={() => setIsVideoPlaying(true)}
                                    onPause={() => setIsVideoPlaying(false)}
                                >
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                                        <p className="text-white">{t('common.loading')}</p>
                                    </div>
                                </video>

                                {/* Наложение для имитации интерактивности */}
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-80 group-hover:opacity-50 transition-opacity"></div>

                                {/* Иконка Play для видео - показывается только если видео не воспроизводится */}
                                {!isVideoPlaying && (
                                    <div
                                        className="absolute inset-0 flex items-center justify-center cursor-pointer"
                                        onClick={() => {
                                            if (videoRef.current) {
                                                videoRef.current.play();
                                            }
                                        }}
                                    >
                                        <div className="w-16 h-16 md:w-20 md:h-20 bg-orange-500/80 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-8 h-8 md:w-10 md:h-10">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Вкладки для категорий возможностей */}
                    <div className="max-w-6xl mx-auto mb-16">
                        <div className="flex flex-wrap justify-center mb-8 gap-2">
                            <button
                                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                                    activeFeaturesTab === 'labs'
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-gray-800/70 text-gray-300 hover:bg-gray-700/70'
                                }`}
                                onClick={() => setActiveFeaturesTab('labs')}
                            >
                                {t('itschools.features.labs.title')}
                            </button>
                            <button
                                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                                    activeFeaturesTab === 'ai'
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-gray-800/70 text-gray-300 hover:bg-gray-700/70'
                                }`}
                                onClick={() => setActiveFeaturesTab('ai')}
                            >
                                {t('itschools.features.ai_tutor.title')}
                            </button>
                            <button
                                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                                    activeFeaturesTab === 'platform'
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-gray-800/70 text-gray-300 hover:bg-gray-700/70'
                                }`}
                                onClick={() => setActiveFeaturesTab('platform')}
                            >
                                {t('itschools.features.platform.title')}
                            </button>
                        </div>

                        {/* Содержимое вкладок */}
                        {activeFeaturesTab === 'labs' && (
                            <div className="bg-gray-800/30 backdrop-blur-sm border border-orange-500/20 p-6 rounded-2xl shadow-lg transform transition-all">
                                <div className="flex flex-col md:flex-row gap-8 items-center">
                                    <div className="md:w-1/2">
                                        <h3 className="text-2xl font-bold mb-4 text-orange-400">{t('itschools.features.labs.title')}</h3>
                                        <p className="text-gray-300 mb-6">
                                            {t('itschools.features.labs.description')}
                                        </p>
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.labs.list_items.microservices')}</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.labs.list_items.databases')}</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.labs.list_items.cloud_services')}</span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="md:w-1/2 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
                                        {!imageErrors['labs_preview'] ? (
                                            <img
                                                src="/images/teacher_workflow.png"
                                                alt="Виртуальные лаборатории"
                                                className="w-full object-cover"
                                                onError={() => handleImageError('labs_preview')}
                                            />
                                        ) : (
                                            <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                                <Database className="h-16 w-16 text-orange-500 opacity-50" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeFeaturesTab === 'ai' && (
                            <div className="bg-gray-800/30 backdrop-blur-sm border border-orange-500/20 p-6 rounded-2xl shadow-lg transform transition-all">
                                <div className="flex flex-col md:flex-row gap-8 items-center">
                                    <div className="md:w-1/2">
                                        <h3 className="text-2xl font-bold mb-4 text-orange-400">{t('itschools.features.ai_tutor.title')}</h3>
                                        <p className="text-gray-300 mb-6">
                                            {t('itschools.features.ai_tutor.description')}
                                        </p>
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.ai_tutor.list_items.personal_tutor')}</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.ai_tutor.list_items.auto_grading')}</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.ai_tutor.list_items.test_generation')}</span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="md:w-1/2 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
                                        {!imageErrors['ai_preview'] ? (
                                            <img
                                                src="/images/student_workflow.png"
                                                alt="AI-поддержка"
                                                className="w-full object-cover"
                                                onError={() => handleImageError('ai_preview')}
                                            />
                                        ) : (
                                            <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                                <Brain className="h-16 w-16 text-orange-500 opacity-50" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeFeaturesTab === 'platform' && (
                            <div className="bg-gray-800/30 backdrop-blur-sm border border-orange-500/20 p-6 rounded-2xl shadow-lg transform transition-all">
                                <div className="flex flex-col md:flex-row gap-8 items-center">
                                    <div className="md:w-1/2">
                                        <h3 className="text-2xl font-bold mb-4 text-orange-400">{t('itschools.features.platform.title')}</h3>
                                        <p className="text-gray-300 mb-6">
                                            {t('itschools.features.platform.description')}
                                        </p>
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.platform.list_items.lms_integration')}</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.platform.list_items.access_management')}</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 mt-1">•</span>
                                                <span className="text-gray-300">{t('itschools.features.platform.list_items.analytics')}</span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="md:w-1/2 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
                                        {!imageErrors['platform_preview'] ? (
                                            <img
                                                src="/images/dvakota_video_poster.jpg"
                                                alt="Единая платформа"
                                                className="w-full object-cover"
                                                onError={() => handleImageError('platform_preview')}
                                            />
                                        ) : (
                                            <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                                <Code className="h-16 w-16 text-orange-500 opacity-50" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* How It Works - Teachers */}
            <section className="py-20 bg-gray-800/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,theme(colors.orange.500/5),transparent_70%)]" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-orange-100 bg-clip-text text-transparent">
                            {t('itschools.how_it_works.teacher.title')}
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            {t('itschools.how_it_works.teacher.subtitle')}
                        </p>
                    </div>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                            <div className="order-2 lg:order-1">
                                <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-xl relative group">
                                    {/* Эффект блика на изображении */}
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500/30 to-orange-600/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>

                                    <div className="relative">
                                        <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                            <School className="h-16 w-16 text-orange-500 opacity-50" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="order-1 lg:order-2">
                                <div className="space-y-8">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            1
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.teacher.step1.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.teacher.step1.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            2
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.teacher.step2.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.teacher.step2.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.teacher.step2.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.teacher.step2.option2')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            3
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.teacher.step3.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.teacher.step3.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            4
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.teacher.step4.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.teacher.step4.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            5
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.teacher.step5.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.teacher.step5.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.teacher.step5.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.teacher.step5.option2')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.teacher.step5.option3')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works - Students */}
            <section className="py-20 bg-gray-900 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-yellow-100 to-orange-100 bg-clip-text text-transparent">
                            {t('itschools.how_it_works.student.title')}
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {t('itschools.how_it_works.student.subtitle')}
                        </p>
                    </div>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <div className="space-y-8">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            1
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.student.step1.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.student.step1.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            2
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.student.step2.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.student.step2.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.student.step2.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.student.step2.option2')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            3
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.student.step3.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.student.step3.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.student.step3.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.student.step3.option2')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.student.step3.option3')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                            4
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-orange-400">
                                                {t('itschools.how_it_works.student.step4.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('itschools.how_it_works.student.step4.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.student.step4.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.student.step4.option2')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-orange-500 mt-1">•</span>
                                                    {t('itschools.how_it_works.student.step4.option3')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-xl relative group">
                                    {/* Эффект блика на изображении */}
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500/30 to-orange-600/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>

                                    <div className="relative">
                                        <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                            <Users className="h-16 w-16 text-orange-500 opacity-50" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section - Redesigned with interactive counters */}
            <section className="py-20 bg-gradient-to-b from-gray-800/40 to-gray-900/40 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,theme(colors.green.500/10),transparent_70%)]" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-green-100 bg-clip-text text-transparent">
                            {t('itschools.benefits.title')}
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            {t('itschools.benefits.subtitle')}
                        </p>
                    </div>

                    {/* Статистика с анимированными счетчиками */}


                    {/* Преимущества в виде интерактивных вкладок */}
                    <div className="max-w-5xl mx-auto bg-gray-800/20 rounded-2xl p-2 backdrop-blur-sm">
                        <div className="flex flex-wrap justify-center mb-6 border-b border-gray-700">
                            <button
                                className={`px-6 py-3 font-semibold rounded-t-lg transition-colors ${activeTab === 'school' ? 'bg-green-500/20 text-green-300' : 'text-gray-400 hover:text-gray-300'}`}
                                onClick={() => setActiveTab('school')}
                            >
                                {t('itschools.tabs.school')}
                            </button>
                            <button
                                className={`px-6 py-3 font-semibold rounded-t-lg transition-colors ${activeTab === 'teachers' ? 'bg-green-500/20 text-green-300' : 'text-gray-400 hover:text-gray-300'}`}
                                onClick={() => setActiveTab('teachers')}
                            >
                                {t('itschools.tabs.teachers')}
                            </button>
                            <button
                                className={`px-6 py-3 font-semibold rounded-t-lg transition-colors ${activeTab === 'students' ? 'bg-green-500/20 text-green-300' : 'text-gray-400 hover:text-gray-300'}`}
                                onClick={() => setActiveTab('students')}
                            >
                                {t('itschools.tabs.students')}
                            </button>
                        </div>

                        <div className="p-6">
                            {activeTab === 'school' && (
                                <ul className="space-y-4">
                                    {benefits.slice(0, 4).map((benefit, index) => (
                                        <li key={index} className="flex items-start gap-4 group">
                                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                                {benefit.icon}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                    {benefit.title}
                                                </h3>
                                                <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                    {benefit.description}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {activeTab === 'teachers' && (
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Lightbulb className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('itschools.teachers_benefits.reduced_workload.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('itschools.teachers_benefits.reduced_workload.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Code className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('itschools.teachers_benefits.ready_templates.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('itschools.teachers_benefits.ready_templates.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <TrendingUp className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('itschools.teachers_benefits.teaching_efficiency.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('itschools.teachers_benefits.teaching_efficiency.description')}
                                            </p>
                                        </div>
                                    </li>
                                </ul>
                            )}

                            {activeTab === 'students' && (
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Brain className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('itschools.students_benefits.personalized_learning.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('itschools.students_benefits.personalized_learning.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Database className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('itschools.students_benefits.real_systems.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('itschools.students_benefits.real_systems.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Award className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('itschools.students_benefits.job_readiness.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('itschools.students_benefits.job_readiness.description')}
                                            </p>
                                        </div>
                                    </li>
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 relative w-full overflow-hidden">
                {/* Фон с органическими формами */}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600/30 to-orange-800/30">
                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                        {/* SVG органические формы */}
                        <svg className="absolute top-0 left-0 w-full h-full opacity-20" viewBox="0 0 1000 1000" preserveAspectRatio="none">
                            <path d="M0,500 C150,300 350,0 500,500 C650,1000 850,700 1000,500 L1000,1000 L0,1000 Z" fill="url(#orange-gradient)" />
                            <defs>
                                <linearGradient id="orange-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#F97316" />
                                    <stop offset="100%" stopColor="#C2410C" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>

                <div className="container relative z-10 mx-auto px-6">
                    <div className="bg-gray-900/80 backdrop-blur-lg border border-orange-500/20 rounded-3xl p-12 md:p-16 shadow-2xl max-w-5xl mx-auto">
                        <h2 className="text-4xl md:text-6xl font-bold mb-6 text-center bg-gradient-to-r from-white to-orange-200 bg-clip-text text-transparent">
                            {t('itschools.cta.title')}
                        </h2>

                        <p className="text-xl text-gray-300 mb-12 text-center max-w-3xl mx-auto">
                            {t('itschools.cta.subtitle')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center">
                            <button
                                onClick={handleRegisterClick}
                                className="group relative px-10 py-5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-700 text-white font-semibold text-lg transition-all hover:scale-105 shadow-lg hover:shadow-orange-500/25"
                            >
                                <span className="relative z-10 flex items-center justify-center gap-3">
                                    {t('itschools.cta.button')}
                                    <Rocket className="w-5 h-5 group-hover:animate-bounce" />
                                </span>
                            </button>

                            <a
                                href="mailto:info@dvakota.com"
                                className="px-10 py-5 rounded-xl text-lg font-semibold border border-white/30 text-white hover:bg-white/10 transition-all shadow-lg flex items-center justify-center gap-2"
                            >
                                {t('itschools.cta.contact_us')}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Auth Dialog */}
            <AuthDialog
                open={showAuthDialog}
                onClose={() => setShowAuthDialog(false)}
                initialMode={initialAuthMode}
                onSuccess={() => {
                    setShowAuthDialog(false);
                }}
            />
        </div>
    );
};

export default ITSchoolsLanding;
